import { getAttachments, uploadAttachment } from "@/services/clientAreaService";
import { AlertTriangle, FileText, Upload } from "lucide-react";
import { DragEvent, useEffect, useRef, useState } from "react";

interface UploadedFile {
  name: string;
  progress: number;
}

interface Attachment {
  anx_codigo: string;
  anx_arquivo: string;
}

interface DocumentUploadProps {
  sigla: string;
  registro: string;
}

const MAX_SIZE = 2 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "application/pdf"];

export default function DocumentUpload({
  sigla,
  registro,
}: DocumentUploadProps) {
  const [queue, setQueue] = useState<UploadedFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [list, setList] = useState<Attachment[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!registro || registro === "temp") {
      return;
    }

    (async () => {
      try {
        const data = await getAttachments(sigla, registro);
        setList(data);
      } catch (error) {
        console.error("Erro ao carregar anexos:", error);
        setList([]);
      }
    })();
  }, [sigla, registro]);

  const validate = (f: File) => {
    if (!ACCEPTED_TYPES.includes(f.type))
      return "Apenas PNG, JPEG ou PDF são permitidos.";
    if (f.size > MAX_SIZE) return "Tamanho máximo: 2 MB.";
    return null;
  };

  const upload = async (file: File, idx: number) => {
    try {
      setQueue((prev) =>
        prev.map((q, i) => (i === idx ? { ...q, progress: 50 } : q)),
      );

      await uploadAttachment(sigla, registro, file);

      setQueue((prev) =>
        prev.map((q, i) => (i === idx ? { ...q, progress: 100 } : q)),
      );

      const data = await getAttachments(sigla, registro);
      setList(data);

      setTimeout(() => {
        setQueue((prev) => prev.filter((_, i) => i !== idx));
      }, 1000);
    } catch (error) {
      console.error("Erro no upload:", error);
      setError("Erro ao fazer upload do arquivo");

      setQueue((prev) => prev.filter((_, i) => i !== idx));
    }
  };

  const handleAdd = (fileList: FileList | null) => {
    if (!fileList) return;
    const arr = Array.from(fileList);
    for (const f of arr) {
      const err = validate(f);
      if (err) {
        setError(`${f.name}: ${err}`);
        return;
      }
    }
    setError(null);
    const uploads = arr.map((f) => ({ name: f.name, progress: 0 }));
    const startIndex = queue.length;
    setQueue((prev) => [...prev, ...uploads]);
    arr.forEach((f, i) => upload(f, startIndex + i));
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleAdd(e.dataTransfer.files);
  };

  return (
    <div className="space-y-4">
      <p className="font-semibold text-n-text-neutral">Seus documentos:</p>
      <p className="text-sm text-n-text-neutral">
        PNG, JPEG ou PDF (máx. 2 MB)
      </p>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={onDrop}
        className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer"
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="w-6 h-6 text-gray-400 mb-2" />
        <span className="text-sm text-n-text-neutral">Arraste e solte</span>
        <input
          type="file"
          accept=".png,.jpeg,.jpg,.pdf"
          multiple
          ref={inputRef}
          className="hidden"
          onChange={(e) => handleAdd(e.target.files)}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm">
          <AlertTriangle className="w-4 h-4" /> {error}
        </div>
      )}

      {queue.map(({ name, progress }) => (
        <div
          key={name}
          className="bg-gray-100 rounded-lg p-4 flex flex-col gap-2"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-n-text-neutral" />
            <span className="text-sm">{name}</span>
          </div>
          <div className="w-full h-2 bg-gray-300 rounded">
            <div
              className="h-full rounded"
              style={{ width: `${progress}%`, backgroundColor: "#A1F7A1" }}
            />
          </div>
        </div>
      ))}

      {list.length > 0 && (
        <div className="space-y-2">
          {list.map((a) => (
            <div key={a.anx_codigo} className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-n-text-neutral" />
              <a
                href={`${import.meta.env.VITE_API_URL}/${a.anx_arquivo}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                {a.anx_arquivo.split("/").pop()}
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
